import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { Upload } from 'lucide-react';
import type { DocumentCategory } from '@/services/api/documentService';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import type { AddDocumentKind, PendingDelete } from '../documentsTypes';
import { DOCUMENT_UPLOAD_ACCEPT } from '../documentsTypes';

interface DocumentsDialogsProps {
  categories: DocumentCategory[];
  activeFolders: DocumentCategory[];
  selectedFolder: DocumentCategory | null;
  parentCategory: DocumentCategory | null;
  formError: string;
  categoryDialogOpen: boolean;
  categoryName: string;
  onCategoryNameChange: (value: string) => void;
  onCloseCategoryDialog: () => void;
  onCreateCategory: () => void;
  creatingCategory: boolean;
  renameFolderOpen: boolean;
  renameFolderName: string;
  onRenameFolderNameChange: (value: string) => void;
  onCloseRenameFolder: () => void;
  onRenameFolder: () => void;
  renamingFolder: boolean;
  moveDocIds: string[];
  moveTargetId: string;
  onMoveTargetChange: (value: string) => void;
  onCloseMove: () => void;
  onConfirmMove: () => void;
  moving: boolean;
  addOpen: boolean;
  addKind: AddDocumentKind;
  addName: string;
  addUrl: string;
  addFile: File | null;
  onAddKindChange: (value: AddDocumentKind) => void;
  onAddNameChange: (value: string) => void;
  onAddUrlChange: (value: string) => void;
  onAddFileChange: (file: File | null) => void;
  onCloseAdd: () => void;
  onSubmitAdd: () => void;
  adding: boolean;
  pendingDelete: PendingDelete | null;
  onClosePendingDelete: () => void;
  onConfirmHideFolder: (id: string) => void;
  onConfirmDeleteFolder: (id: string) => void;
  onConfirmDeleteDocument: (id: string) => void;
  onConfirmBulkDelete: (ids: string[]) => void;
}

const DocumentsDialogs: React.FC<DocumentsDialogsProps> = ({
  categories,
  activeFolders,
  selectedFolder,
  parentCategory,
  formError,
  categoryDialogOpen,
  categoryName,
  onCategoryNameChange,
  onCloseCategoryDialog,
  onCreateCategory,
  creatingCategory,
  renameFolderOpen,
  renameFolderName,
  onRenameFolderNameChange,
  onCloseRenameFolder,
  onRenameFolder,
  renamingFolder,
  moveDocIds,
  moveTargetId,
  onMoveTargetChange,
  onCloseMove,
  onConfirmMove,
  moving,
  addOpen,
  addKind,
  addName,
  addUrl,
  addFile,
  onAddKindChange,
  onAddNameChange,
  onAddUrlChange,
  onAddFileChange,
  onCloseAdd,
  onSubmitAdd,
  adding,
  pendingDelete,
  onClosePendingDelete,
  onConfirmHideFolder,
  onConfirmDeleteFolder,
  onConfirmDeleteDocument,
  onConfirmBulkDelete,
}) => (
  <>
    <Dialog open={categoryDialogOpen} onClose={onCloseCategoryDialog} fullWidth maxWidth="xs">
      <DialogTitle>{parentCategory ? `New folder in ${parentCategory.name}` : 'New folder'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          placeholder={parentCategory ? 'e.g. Templates' : 'e.g. SOP'}
          fullWidth
          value={categoryName}
          onChange={(e) => onCategoryNameChange(e.target.value)}
        />
        {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseCategoryDialog}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!categoryName.trim() || creatingCategory}
          onClick={onCreateCategory}
        >
          {creatingCategory ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={renameFolderOpen} onClose={onCloseRenameFolder} fullWidth maxWidth="xs">
      <DialogTitle>Rename folder</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={renameFolderName}
          onChange={(e) => onRenameFolderNameChange(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseRenameFolder}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!renameFolderName.trim() || renamingFolder}
          onClick={onRenameFolder}
        >
          {renamingFolder ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={moveDocIds.length > 0} onClose={onCloseMove} fullWidth maxWidth="xs">
      <DialogTitle>
        Move {moveDocIds.length === 1 ? 'file' : `${moveDocIds.length} files`}
      </DialogTitle>
      <DialogContent>
        <AppSelect
          label="Destination folder"
          value={moveTargetId}
          onChange={(e) => onMoveTargetChange(String(e.target.value))}
          sx={{ mt: 1 }}
        >
          {activeFolders.map((folder) => (
            <AppMenuItem key={folder.id} value={folder.id}>
              {folder.name}
            </AppMenuItem>
          ))}
        </AppSelect>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseMove}>Cancel</Button>
        <Button variant="contained" disabled={!moveTargetId || moving} onClick={onConfirmMove}>
          {moving ? 'Moving…' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={addOpen} onClose={onCloseAdd} fullWidth maxWidth="sm">
      <DialogTitle>Add to {selectedFolder?.name || categories[0]?.name || 'folder'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 1 }}>
          <AppSelect
            label="Type"
            value={addKind}
            onChange={(e) => onAddKindChange(e.target.value as AddDocumentKind)}
          >
            <AppMenuItem value="upload">Upload file (PDF, Excel, Word, image)</AppMenuItem>
            <AppMenuItem value="google_doc">Create Google Doc</AppMenuItem>
            <AppMenuItem value="google_sheet">Create Google Sheet</AppMenuItem>
            <AppMenuItem value="drive_link">Paste existing Drive link</AppMenuItem>
          </AppSelect>
          <TextField
            label={addKind === 'upload' ? 'Display name (optional)' : 'Name'}
            value={addName}
            onChange={(e) => onAddNameChange(e.target.value)}
            placeholder={
              addKind === 'google_sheet'
                ? 'Untitled spreadsheet'
                : addKind === 'google_doc'
                  ? 'Untitled document'
                  : ''
            }
            fullWidth
          />
          {addKind === 'upload' && (
            <Button component="label" variant="outlined" startIcon={<Upload size={16} />}>
              {addFile ? addFile.name : 'Choose file'}
              <input
                type="file"
                hidden
                accept={DOCUMENT_UPLOAD_ACCEPT}
                onChange={(e) => onAddFileChange(e.target.files?.[0] ?? null)}
              />
            </Button>
          )}
          {addKind === 'drive_link' && (
            <TextField
              label="Drive link"
              value={addUrl}
              onChange={(e) => onAddUrlChange(e.target.value)}
              placeholder="https://drive.google.com/..."
              fullWidth
              required
            />
          )}
          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseAdd}>Cancel</Button>
        <Button variant="contained" disabled={adding} onClick={onSubmitAdd}>
          {adding ? 'Saving…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>

    <ConfirmationModal
      open={!!pendingDelete}
      onClose={onClosePendingDelete}
      data={
        pendingDelete
          ? pendingDelete.kind === 'folder-hide'
            ? {
                title: 'Hide folder',
                message: `Hide folder “${pendingDelete.name}”? It still has files, so it will be hidden instead of deleted. Files stay in Drive.`,
                confirmText: 'Hide',
                type: 'danger',
                onConfirm: () => {
                  onConfirmHideFolder(pendingDelete.id);
                  onClosePendingDelete();
                },
              }
            : pendingDelete.kind === 'folder-delete'
              ? {
                  title: 'Delete folder',
                  message: `Permanently delete empty folder “${pendingDelete.name}” and its empty subfolders? This also trashes them in Google Drive.`,
                  confirmText: 'Delete',
                  type: 'danger',
                  onConfirm: () => {
                    onConfirmDeleteFolder(pendingDelete.id);
                    onClosePendingDelete();
                  },
                }
              : pendingDelete.kind === 'bulk'
                ? {
                    title: 'Delete documents',
                    message: `Remove ${pendingDelete.count} document${pendingDelete.count === 1 ? '' : 's'} from the library and trash in Drive?`,
                    confirmText: 'Delete',
                    type: 'danger',
                    onConfirm: () => {
                      onConfirmBulkDelete(pendingDelete.ids);
                      onClosePendingDelete();
                    },
                  }
                : {
                    title: 'Delete document',
                    message: `Remove “${pendingDelete.name}” from the library and trash it in Drive?`,
                    confirmText: 'Delete',
                    type: 'danger',
                    onConfirm: () => {
                      onConfirmDeleteDocument(pendingDelete.id);
                      onClosePendingDelete();
                    },
                  }
          : undefined
      }
    />
  </>
);

export default DocumentsDialogs;
